import { booleans, extrusions, primitives, transforms } from "@jscad/modeling";
import type { HandlePlacement, StorageDesign, WallFace } from "../domain/design";
import { getHandleAxisCenter, getHandleLength, getHandleOffset, normalizeDesign } from "../domain/design";

const { cuboid, cylinder, polygon, roundedRectangle } = primitives;
const { extrudeLinear } = extrusions;
const { subtract, union } = booleans;
const { rotateX, rotateY, translate } = transforms;

type Geometry = ReturnType<typeof cuboid>;

export function createTrayGeometry(design: StorageDesign): Geometry {
  const d = normalizeDesign(design);
  const outer = box(
    [d.widthMm, d.depthMm, d.heightMm],
    [0, 0, d.heightMm / 2],
    d.cornerRadiusMm
  );
  const innerWidth = Math.max(1, d.widthMm - d.wallMm * 2);
  const innerDepth = Math.max(1, d.depthMm - d.wallMm * 2);
  const innerHeight = d.hasBottom ? d.heightMm - d.bottomMm + 0.4 : d.heightMm + 0.8;
  const innerZ = d.hasBottom ? d.bottomMm + innerHeight / 2 : d.heightMm / 2;
  const innerCornerCut = Math.max(0, d.cornerRadiusMm - d.wallMm);
  const inner = chamferedBox([innerWidth, innerDepth, innerHeight], [0, 0, innerZ], innerCornerCut);
  let shell = subtract(outer, inner) as Geometry;

  const handleCutouts = createHandleCutouts(d);

  if (handleCutouts.length > 0) {
    shell = subtract(shell, ...handleCutouts) as Geometry;
  }

  const parts: Geometry[] = [shell];
  const dividerHeight = d.dividerHeightMm;
  const dividerCenterZ = (d.hasBottom ? d.bottomMm : 0) + dividerHeight / 2;

  d.dividerSegments.forEach((segment) => {
    if (segment.axis === "column") {
      const x = -innerWidth / 2 + innerWidth * segment.fraction;
      const segmentStart = -innerDepth / 2 + innerDepth * segment.startFraction;
      const segmentEnd = -innerDepth / 2 + innerDepth * segment.endFraction;
      const segmentDepth = Math.max(1, segmentEnd - segmentStart);
      parts.push(
        cuboid({
          size: [d.dividerMm, segmentDepth, dividerHeight],
          center: [x, segmentStart + segmentDepth / 2, dividerCenterZ]
        })
      );
      return;
    }

    const y = -innerDepth / 2 + innerDepth * segment.fraction;
    const segmentStart = -innerWidth / 2 + innerWidth * segment.startFraction;
    const segmentEnd = -innerWidth / 2 + innerWidth * segment.endFraction;
    const segmentWidth = Math.max(1, segmentEnd - segmentStart);
    parts.push(
      cuboid({
        size: [segmentWidth, d.dividerMm, dividerHeight],
        center: [segmentStart + segmentWidth / 2, y, dividerCenterZ]
      })
    );
  });

  return union(parts) as Geometry;
}

function createHandleCutouts(d: StorageDesign): Geometry[] {
  if (d.handleStyle === "none") {
    return [];
  }

  return d.handlePlacements.map((placement) =>
    d.handleStyle === "slot" ? createSlotCutout(d, placement) : createNotchCutout(d, placement.face)
  );
}

function createNotchCutout(d: StorageDesign, face: WallFace): Geometry {
  const axisLength = face === "front" || face === "back" ? d.widthMm : d.depthMm;
  const notchLength = getHandleLength(axisLength, d.handleLengthMm);
  const notchHeight = Math.min(18, d.heightMm * 0.62);
  const axisCenter = getHandleAxisCenter(axisLength, notchLength, getHandleOffset(d, face));
  const z = d.heightMm - notchHeight / 2 + 0.2;

  if (face === "front" || face === "back") {
    const y = face === "front" ? -d.depthMm / 2 + d.wallMm / 2 : d.depthMm / 2 - d.wallMm / 2;

    return cuboid({
      size: [notchLength, d.wallMm + 1.6, notchHeight],
      center: [axisCenter, y, z]
    });
  }

  const x = face === "left" ? -d.widthMm / 2 + d.wallMm / 2 : d.widthMm / 2 - d.wallMm / 2;

  return cuboid({
    size: [d.wallMm + 1.6, notchLength, notchHeight],
    center: [x, axisCenter, z]
  });
}

function createSlotCutout(d: StorageDesign, placement: HandlePlacement): Geometry {
  const face = placement.face;
  const axisLength = face === "front" || face === "back" ? d.widthMm : d.depthMm;
  const slotLength = getHandleLength(axisLength, d.handleLengthMm);
  const slotHeight = Math.min(14, d.heightMm * 0.38);
  const axisCenter = getHandleAxisCenter(axisLength, slotLength, placement.offset);
  const slotCoreLength = Math.max(1, slotLength - slotHeight);
  const z = slotHeight / 2 + Math.max(0, d.heightMm - slotHeight) * (placement.verticalOffset ?? 0.58);

  if (face === "front" || face === "back") {
    const y = face === "front" ? -d.depthMm / 2 + d.wallMm / 2 : d.depthMm / 2 - d.wallMm / 2;
    const center: [number, number, number] = [axisCenter, y, z];
    const left = translate(
      [axisCenter - slotCoreLength / 2, y, z],
      rotateX(Math.PI / 2, cylinder({ radius: slotHeight / 2, height: d.wallMm + 2.2, segments: 24 }))
    ) as Geometry;
    const right = translate(
      [axisCenter + slotCoreLength / 2, y, z],
      rotateX(Math.PI / 2, cylinder({ radius: slotHeight / 2, height: d.wallMm + 2.2, segments: 24 }))
    ) as Geometry;
    const middle = cuboid({ size: [slotCoreLength, d.wallMm + 2.2, slotHeight], center });

    return union([middle, left, right]) as Geometry;
  }

  const x = face === "left" ? -d.widthMm / 2 + d.wallMm / 2 : d.widthMm / 2 - d.wallMm / 2;
  const center: [number, number, number] = [x, axisCenter, z];
  const bottom = translate(
    [x, axisCenter - slotCoreLength / 2, z],
    rotateY(Math.PI / 2, cylinder({ radius: slotHeight / 2, height: d.wallMm + 2.2, segments: 24 }))
  ) as Geometry;
  const top = translate(
    [x, axisCenter + slotCoreLength / 2, z],
    rotateY(Math.PI / 2, cylinder({ radius: slotHeight / 2, height: d.wallMm + 2.2, segments: 24 }))
  ) as Geometry;
  const middle = cuboid({ size: [d.wallMm + 2.2, slotCoreLength, slotHeight], center });

  return union([middle, bottom, top]) as Geometry;
}

function box(size: [number, number, number], center: [number, number, number], radius: number): Geometry {
  if (radius <= 0) {
    return cuboid({ size, center });
  }

  const roundRadius = Math.min(radius, size[0] / 2 - 0.1, size[1] / 2 - 0.1);
  const footprint = roundedRectangle({
    size: [size[0], size[1]],
    roundRadius,
    segments: 24
  });

  return translate(
    [center[0], center[1], center[2] - size[2] / 2],
    extrudeLinear({ height: size[2] }, footprint)
  ) as Geometry;
}

function chamferedBox(
  size: [number, number, number],
  center: [number, number, number],
  cornerCut: number
): Geometry {
  const halfWidth = size[0] / 2;
  const halfDepth = size[1] / 2;
  const c = Math.max(0, Math.min(cornerCut, halfWidth - 0.1, halfDepth - 0.1));

  if (c <= 0) {
    return cuboid({ size, center });
  }

  const footprint = polygon({
    points: [
      [-halfWidth + c, -halfDepth],
      [halfWidth - c, -halfDepth],
      [halfWidth, -halfDepth + c],
      [halfWidth, halfDepth - c],
      [halfWidth - c, halfDepth],
      [-halfWidth + c, halfDepth],
      [-halfWidth, halfDepth - c],
      [-halfWidth, -halfDepth + c]
    ]
  });

  return translate(
    [center[0], center[1], center[2] - size[2] / 2],
    extrudeLinear({ height: size[2] }, footprint)
  ) as Geometry;
}
